import { useState } from "react";
import * as DocumentPicker from "expo-document-picker";
import { Alert, View } from "react-native";
import { useRouter } from "expo-router";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { AppText, Button, ButtonText, TextField } from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceProjects,
  useWorkspaceQuotes,
} from "~/hooks/workspace";
import { formatCurrency } from "~/utils/format";
import { removeFile, signedFileUrl, uploadFile } from "~/api/uploads";
import { db } from "~/api/db";

function safeFilename(value: string) {
  return value.replace(/[^a-zA-Z0-9._-]/g, "-").slice(-120);
}

function quoteLines(
  items: {
    description: string;
    quantity: number;
    unit_amount_cents: number;
    gst_rate: number;
  }[],
) {
  return items
    .map(
      (item) =>
        `${item.description} | ${item.quantity} | ${(item.unit_amount_cents / 100).toFixed(2)} | ${item.gst_rate}`,
    )
    .join("\n");
}

function invoiceDueDate() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function todayPlus(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export default function QuotesScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const workspace = usePrimaryWorkspace();
  const workspaceId = workspace.data?.id;
  const { readOnly } = useWorkspaceReadOnly(workspaceId);
  const clients = useWorkspaceClients(workspaceId);
  const projects = useWorkspaceProjects(workspaceId);
  const quotes = useWorkspaceQuotes(workspaceId);
  const [scheduleDate, setScheduleDate] = useState(todayPlus(1));
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [schedulingQuoteId, setSchedulingQuoteId] = useState<string | null>(
    null,
  );
  const clientById = new Map(
    (clients.data ?? []).map((client) => [client.id, client]),
  );
  const projectById = new Map(
    (projects.data ?? []).map((project) => [project.id, project]),
  );

  async function pickQuoteAttachment(quoteId: string) {
    if (!user || !workspaceId) return;
    const quote = quotes.quotes.find((row) => row.id === quoteId);
    if (!quote) return;
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      copyToCacheDirectory: true,
    });
    if (result.canceled) return;
    const asset = result.assets[0];
    if (!asset) return;
    if (asset.size && asset.size > 10 * 1024 * 1024) {
      Alert.alert("File too large", "Choose a file up to 10 MB.");
      return;
    }
    const mimeType = asset.mimeType ?? "application/octet-stream";
    const fileBody = asset.file
      ? await asset.file.arrayBuffer()
      : await (await fetch(asset.uri)).arrayBuffer();
    let path: string;
    try {
      ({ path } = await uploadFile({
        workspaceId,
        purpose: "attachment",
        body: fileBody,
        mimeType,
      }));
    } catch (error) {
      Alert.alert(
        "Upload failed",
        error instanceof Error ? error.message : "Try again.",
      );
      return;
    }
    if (quote.attachment_path && quote.attachment_path !== path) {
      await removeFile(quote.attachment_path);
    }
    await quotes.updateAttachment.mutateAsync({
      id: quoteId,
      attachmentPath: path,
      attachmentFilename: asset.name,
      attachmentMimeType: mimeType,
      attachmentSizeBytes: asset.size ?? fileBody.byteLength,
    });
  }

  async function removeQuoteAttachment(quoteId: string) {
    const quote = quotes.quotes.find((row) => row.id === quoteId);
    if (!quote?.attachment_path) return;
    await removeFile(quote.attachment_path);
    await quotes.updateAttachment.mutateAsync({
      id: quoteId,
      attachmentPath: null,
      attachmentFilename: null,
      attachmentMimeType: null,
      attachmentSizeBytes: null,
    });
  }

  const fields = [
    { key: "title", label: "Quote title", placeholder: "Website care plan" },
    {
      key: "clientId",
      label: "Client",
      placeholder: "Choose client",
      options: [
        { label: "No client", value: "" },
        ...(clients.data ?? []).map((client) => ({
          label: client.name,
          value: client.id,
        })),
      ],
      clearOnChange: ["projectId"],
    },
    {
      key: "projectId",
      label: "Project",
      placeholder: "Choose project",
      optionsForValues: (values: Record<string, string>) => [
        { label: "No project", value: "" },
        ...(projects.data ?? [])
          .filter(
            (project) =>
              !values.clientId || project.client_id === values.clientId,
          )
          .map((project) => ({ label: project.name, value: project.id })),
      ],
    },
    { key: "validUntil", label: "Valid until", placeholder: "YYYY-MM-DD" },
    {
      key: "purchaseOrderReference",
      label: "Purchase order",
      placeholder: "PO-1042",
    },
    {
      key: "clientReference",
      label: "Client reference",
      placeholder: "Job or cost centre",
    },
    {
      key: "jobStartsOn",
      label: "Job starts",
      placeholder: "YYYY-MM-DD",
    },
    {
      key: "jobEndsOn",
      label: "Job ends",
      placeholder: "YYYY-MM-DD",
    },
    {
      key: "lineItems",
      label: "Line items",
      placeholder: "Website audit | 1 | 499.00 | 15",
      multiline: true,
    },
    {
      key: "terms",
      label: "Terms",
      placeholder: "Deposit, scope and acceptance terms",
      multiline: true,
    },
    {
      key: "notes",
      label: "Notes",
      placeholder: "Client-facing notes",
      multiline: true,
    },
  ];

  return (
    <ResourceListScreen
      title="Quotes & estimates"
      subtitle="Price work clearly, record acceptance, then convert the accepted quote into a draft invoice. Line format: description | quantity | unit price | GST %."
      primaryAction="Create quote"
      emptyTitle="Quotes"
      emptyDescription="Create the first estimate for upcoming client work."
      fields={fields}
      requiredField="title"
      readOnly={readOnly}
      metrics={[
        {
          label: "Open",
          value: String(
            quotes.quotes.filter((quote) =>
              ["draft", "sent", "viewed"].includes(quote.status),
            ).length,
          ),
        },
        {
          label: "Accepted",
          value: String(
            quotes.quotes.filter((quote) => quote.status === "accepted").length,
          ),
        },
        {
          label: "Pipeline value",
          value: formatCurrency(
            quotes.quotes
              .filter(
                (quote) =>
                  !["declined", "expired", "converted"].includes(quote.status),
              )
              .reduce((sum, quote) => sum + quote.total_cents, 0) / 100,
          ),
        },
      ]}
      items={quotes.quotes.map((quote) => {
        const clientEmail = clientById.get(quote.client_id ?? "")?.email;
        const canSchedule =
          quote.status === "draft" &&
          Boolean(clientEmail) &&
          quote.quote_items.length > 0;
        const isScheduled = quote.scheduled_send_status === "scheduled";

        return {
          id: quote.id,
          eyebrow: `${quote.quote_number} · ${quote.status}${
            isScheduled ? " · send scheduled" : ""
          }`,
          title: quote.title,
          description: quote.quote_items
            .map((item) => item.description)
            .join(", "),
          meta: [
            clientById.get(quote.client_id ?? "")?.name ?? "No client",
            projectById.get(quote.project_id ?? "")?.name ?? "No project",
            formatCurrency(quote.total_cents / 100, quote.currency),
            quote.valid_until ? `Valid to ${quote.valid_until}` : "No expiry",
            quote.attachment_filename
              ? `Attachment: ${quote.attachment_filename}`
              : "No attachment",
            ...(isScheduled && quote.scheduled_send_at
              ? [`Send ${new Date(quote.scheduled_send_at).toLocaleString()}`]
              : []),
          ],
          editValues:
            quote.status === "draft"
              ? {
                  title: quote.title,
                  clientId: quote.client_id ?? "",
                  projectId: quote.project_id ?? "",
                  validUntil: quote.valid_until ?? "",
                  purchaseOrderReference: quote.purchase_order_reference ?? "",
                  clientReference: quote.client_reference ?? "",
                  jobStartsOn: quote.job_starts_on ?? "",
                  jobEndsOn: quote.job_ends_on ?? "",
                  lineItems: quoteLines(quote.quote_items),
                  terms: quote.terms ?? "",
                  notes: quote.notes ?? "",
                }
              : undefined,
          actions: readOnly ? undefined : (
            <>
              {quote.status === "draft" ? (
                <>
                  <Button
                    compact
                    disabled={quotes.sendEmail.isPending}
                    onPress={() => quotes.sendEmail.mutate(quote.id)}
                  >
                    <ButtonText>Send to client</ButtonText>
                  </Button>
                  <Button
                    compact
                    variant="outline"
                    onPress={() =>
                      quotes.setStatus.mutate({ id: quote.id, status: "sent" })
                    }
                  >
                    <ButtonText>Mark sent manually</ButtonText>
                  </Button>
                  <Button
                    compact
                    variant="outline"
                    disabled={quotes.updateAttachment.isPending}
                    onPress={() => void pickQuoteAttachment(quote.id)}
                  >
                    <ButtonText>
                      {quote.attachment_filename
                        ? "Replace attachment"
                        : "Attach file"}
                    </ButtonText>
                  </Button>
                  {quote.attachment_filename ? (
                    <Button
                      compact
                      variant="outline"
                      disabled={quotes.updateAttachment.isPending}
                      onPress={() => void removeQuoteAttachment(quote.id)}
                    >
                      <ButtonText>Remove attachment</ButtonText>
                    </Button>
                  ) : null}
                  {isScheduled ? (
                    <Button
                      compact
                      variant="outline"
                      disabled={quotes.cancelScheduledSend.isPending}
                      onPress={() =>
                        quotes.cancelScheduledSend.mutate(quote.id)
                      }
                    >
                      <ButtonText>Cancel scheduled send</ButtonText>
                    </Button>
                  ) : canSchedule ? (
                    schedulingQuoteId === quote.id ? (
                      <View style={{ gap: 8, minWidth: 220 }}>
                        <AppText size="xs" weight="semibold">
                          Schedule send (NZ +12:00)
                        </AppText>
                        <TextField
                          value={scheduleDate}
                          placeholder="YYYY-MM-DD"
                          onChangeText={setScheduleDate}
                        />
                        <TextField
                          value={scheduleTime}
                          placeholder="HH:mm"
                          onChangeText={setScheduleTime}
                        />
                        <View
                          style={{
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 8,
                          }}
                        >
                          <Button
                            compact
                            disabled={quotes.scheduleSend.isPending}
                            onPress={() =>
                              void quotes.scheduleSend
                                .mutateAsync({
                                  id: quote.id,
                                  date: scheduleDate,
                                  time: scheduleTime,
                                })
                                .then(() => setSchedulingQuoteId(null))
                            }
                          >
                            <ButtonText>
                              {quotes.scheduleSend.isPending
                                ? "Scheduling..."
                                : "Confirm schedule"}
                            </ButtonText>
                          </Button>
                          <Button
                            compact
                            variant="outline"
                            onPress={() => setSchedulingQuoteId(null)}
                          >
                            <ButtonText>Close</ButtonText>
                          </Button>
                        </View>
                      </View>
                    ) : (
                      <Button
                        compact
                        variant="outline"
                        onPress={() => {
                          setSchedulingQuoteId(quote.id);
                          setScheduleDate(todayPlus(1));
                          setScheduleTime("09:00");
                        }}
                      >
                        <ButtonText>Schedule send</ButtonText>
                      </Button>
                    )
                  ) : null}
                  {quote.scheduled_send_error ? (
                    <AppText size="xs" tone="danger">
                      {quote.scheduled_send_error}
                    </AppText>
                  ) : null}
                </>
              ) : null}
              {["sent", "viewed"].includes(quote.status) ? (
                <>
                  <Button
                    compact
                    onPress={() =>
                      quotes.setStatus.mutate({
                        id: quote.id,
                        status: "accepted",
                      })
                    }
                  >
                    <ButtonText>Accept</ButtonText>
                  </Button>
                  <Button
                    compact
                    variant="outline"
                    onPress={() =>
                      quotes.setStatus.mutate({
                        id: quote.id,
                        status: "declined",
                      })
                    }
                  >
                    <ButtonText>Decline</ButtonText>
                  </Button>
                </>
              ) : null}
              {quote.status === "accepted" ? (
                <Button
                  compact
                  onPress={() =>
                    quotes.convertToInvoice.mutate(
                      { id: quote.id, dueDate: invoiceDueDate() },
                      {
                        onSuccess: (invoice) => {
                          const row = Array.isArray(invoice)
                            ? invoice[0]
                            : invoice;
                          if (row?.id) {
                            router.push(`/invoices/${row.id}` as never);
                            return;
                          }
                          Alert.alert(
                            "Conversion incomplete",
                            "The invoice was created but could not be opened.",
                          );
                        },
                        onError: (error) => {
                          Alert.alert(
                            "Unable to convert quote",
                            error instanceof Error
                              ? error.message
                              : "Conversion failed.",
                          );
                        },
                      },
                    )
                  }
                >
                  <ButtonText>Convert to invoice</ButtonText>
                </Button>
              ) : null}
              {quote.status === "converted" && quote.converted_invoice_id ? (
                <Button
                  compact
                  variant="outline"
                  onPress={() =>
                    router.push(
                      `/invoices/${quote.converted_invoice_id}` as never,
                    )
                  }
                >
                  <ButtonText>Open invoice</ButtonText>
                </Button>
              ) : null}
              {quote.status === "draft" ? (
                <Button
                  compact
                  variant="outline"
                  onPress={() =>
                    Alert.alert("Delete draft quote?", quote.quote_number, [
                      { text: "Cancel", style: "cancel" },
                      {
                        text: "Delete",
                        style: "destructive",
                        onPress: () => quotes.remove.mutate(quote.id),
                      },
                    ])
                  }
                >
                  <ButtonText>Delete</ButtonText>
                </Button>
              ) : null}
            </>
          ),
        };
      })}
      loading={
        workspace.isLoading ||
        clients.isLoading ||
        projects.isLoading ||
        quotes.isLoading
      }
      error={
        quotes.error ??
        quotes.create.error ??
        quotes.update.error ??
        quotes.sendEmail.error ??
        quotes.scheduleSend.error ??
        quotes.cancelScheduledSend.error ??
        quotes.setStatus.error ??
        quotes.convertToInvoice.error ??
        quotes.remove.error
      }
      creating={quotes.create.isPending}
      updating={quotes.update.isPending}
      onCreate={(values) => quotes.create.mutateAsync(values)}
      onUpdate={(id, values) => quotes.update.mutateAsync({ id, values })}
    />
  );
}
